import { createSlice } from "@reduxjs/toolkit";

import {
  submitPayment,
  getMyTeamPayments,
  getTeamPeriods,
  getPeriodDetails,
  getPendingPayments,
  approvePayment,
  rejectPayment,
  initializePaymentPeriod,
} from "../thunks/TeamPayment";

const teamPaymentSlice = createSlice({
  name: "teamPayment",
  initialState: {
    myTeamPeriods: [], // For Team Admin
    teamPeriods: [], // For Superadmin viewing specific team
    selectedPeriod: null, // Detailed period view
    pendingPayments: [], // All pending payments for Superadmin
    isLoading: false,
    isSubmitting: false,
    error: null,
    lastAction: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedPeriod: (state) => {
      state.selectedPeriod = null;
    },
    clearLastAction: (state) => {
      state.lastAction = null;
    },
  },
  extraReducers(builder) {
    // Submit Payment
    builder
      .addCase(submitPayment.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(submitPayment.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.lastAction = "payment_submitted";
        // Update myTeamPeriods if the period exists
        const updatedPeriod = action.payload.data.period;
        const index = state.myTeamPeriods.findIndex(
          (p) => p._id === updatedPeriod._id
        );
        if (index !== -1) {
          state.myTeamPeriods[index] = updatedPeriod;
        }
      })
      .addCase(submitPayment.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload;
      });

    // Get My Team Payments
    builder
      .addCase(getMyTeamPayments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getMyTeamPayments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.myTeamPeriods = action.payload.data.periods;
      })
      .addCase(getMyTeamPayments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Get Team Periods (Superadmin)
    builder
      .addCase(getTeamPeriods.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getTeamPeriods.fulfilled, (state, action) => {
        state.isLoading = false;
        state.teamPeriods = action.payload.data.periods;
      })
      .addCase(getTeamPeriods.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Get Period Details
    builder
      .addCase(getPeriodDetails.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPeriodDetails.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedPeriod = action.payload.data.period;
      })
      .addCase(getPeriodDetails.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Get Pending Payments
    builder
      .addCase(getPendingPayments.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getPendingPayments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.pendingPayments = action.payload.data.pendingPayments;
      })
      .addCase(getPendingPayments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      });

    // Approve Payment
    builder
      .addCase(approvePayment.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(approvePayment.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.lastAction = "payment_approved";
        // Update selected period if it's the same
        const updatedPeriod = action.payload.data.period;
        if (
          state.selectedPeriod &&
          state.selectedPeriod._id === updatedPeriod._id
        ) {
          state.selectedPeriod = updatedPeriod;
        }
        // Remove from pending payments
        const approvedPaymentId = action.meta.arg.paymentId;
        state.pendingPayments = state.pendingPayments.filter(
          (p) => p.paymentId !== approvedPaymentId
        );
      })
      .addCase(approvePayment.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload;
      });

    // Reject Payment
    builder
      .addCase(rejectPayment.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(rejectPayment.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.lastAction = "payment_rejected";
        // Update selected period if it's the same
        const updatedPeriod = action.payload.data.period;
        if (
          state.selectedPeriod &&
          state.selectedPeriod._id === updatedPeriod._id
        ) {
          state.selectedPeriod = updatedPeriod;
        }
        // Remove from pending payments
        const rejectedPaymentId = action.meta.arg.paymentId;
        state.pendingPayments = state.pendingPayments.filter(
          (p) => p.paymentId !== rejectedPaymentId
        );
      })
      .addCase(rejectPayment.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload;
      });

    // Initialize Payment Period
    builder
      .addCase(initializePaymentPeriod.pending, (state) => {
        state.isSubmitting = true;
        state.error = null;
      })
      .addCase(initializePaymentPeriod.fulfilled, (state) => {
        state.isSubmitting = false;
        state.lastAction = "period_initialized";
      })
      .addCase(initializePaymentPeriod.rejected, (state, action) => {
        state.isSubmitting = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearSelectedPeriod, clearLastAction } =
  teamPaymentSlice.actions;
export const teamPaymentReducer = teamPaymentSlice.reducer;




